import { Context, Effect, Layer, Schema, Scope } from "effect";
import { AtomRegistry, type Atom, type AsyncResult } from "effect/unstable/reactivity";
import type { BrowserObservationEvent } from "@workerstation/shared/browser-observability-model";
import { resourceUnavailable } from "@workerstation/shared/errors/resource";
import { BrowserApi, type BrowserApiShape } from "../api/observability/service.ts";
import { QueryWork } from "../state/query-work.ts";
import { makeSessionOwner } from "../state/session-owner.ts";
import { makeRecordingOwner } from "./owner.ts";
export const RecordingLaneInput = Schema.Struct({
    workspaceId: Schema.String,
    sessionId: Schema.optionalKey(Schema.String),
    startMs: Schema.Finite,
    endMs: Schema.Finite
});
export type RecordingLaneInput = typeof RecordingLaneInput.Type;
export const recordingLaneKey = (input: RecordingLaneInput) => JSON.stringify([input.workspaceId, input.sessionId, input.startMs, input.endMs]);
export interface RecordingLaneView {
    readonly events: readonly BrowserObservationEvent[] | null;
    readonly error: string | null;
    readonly closed: boolean;
}
export interface RecordingLaneSession {
    readonly snapshot: Atom.Atom<AsyncResult.AsyncResult<RecordingLaneView>>;
    readonly read: Effect.Effect<RecordingLaneView>;
}
export class RecordingLaneSessions extends Context.Service<RecordingLaneSessions, {
    readonly open: (input: RecordingLaneInput) => Effect.Effect<RecordingLaneSession, never, Scope.Scope>;
}>()("workerstation/frontend/RecordingLaneSessions") {
}
export const makeRecordingLanes = Effect.fn("Frontend.Recording.lanes")(function* (root: Scope.Scope, input: RecordingLaneInput, ports: {
    readonly api: BrowserApiShape;
    readonly registry: AtomRegistry.AtomRegistry;
    readonly queryWork: typeof QueryWork.Service;
}) {
    const initial: RecordingLaneView = { events: null, error: null, closed: false };
    const life = yield* makeRecordingOwner(root, initial, ports, value => value);
    if (input.sessionId) {
        const load = Schema.decodeUnknownEffect(RecordingLaneInput)(input).pipe(Effect.mapError(() => resourceUnavailable()), Effect.flatMap(value => ports.api.context(value.workspaceId, {
            sessionId: input.sessionId ?? "",
            since: Math.max(0, Math.floor(value.startMs) - 2000),
            before: Math.ceil(value.endMs) + 2000
        })), Effect.map(snapshot => [...snapshot.events].sort((a, b) => a.timestamp - b.timestamp)));
        yield* life.queries.start(life.respond(load), reply => life.update(current => !life.alive() ? current
            : reply._tag === "Success" ? { ...current, events: reply.value, error: null }
                : { ...current, error: reply.message }));
    }
    return { snapshot: life.snapshot, read: life.read } satisfies RecordingLaneSession;
});
export const RecordingLaneSessionsLive = (root: Scope.Scope) => Layer.effect(RecordingLaneSessions, Effect.gen(function* () {
    const owner = yield* makeSessionOwner(root);
    const ports = { api: yield* BrowserApi, registry: yield* AtomRegistry.AtomRegistry, queryWork: yield* QueryWork };
    return RecordingLaneSessions.of({ open: input => owner.open(makeRecordingLanes(root, input, ports)) });
}));
